import { FC, useState } from "react";
import { MiList, MiModalConfirm } from "@miplatform/components";
import {
  BUTTON_LABELS,
  CONFIG_PAGES,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import {
  IDepartmentResponse,
  IVerticalTabProps,
  PlatformModules,
} from "@miplatform/types";
import { AddButton, VerticalTabsLayout } from "@mibuildweb/components";
import {
  useDepartment,
  useDepartments,
  usePermissions,
} from "@mibuildweb/hooks";
import { ConfigDepartmentListCard } from "./components/ConfigDepartmentListCard";
import { CONFIG_PATHS } from "../../../routes/config/constants";
import { ConfigPageLayout } from "../ConfigPageLayout";

const departmentListTabs: IVerticalTabProps<IDepartmentResponse[]>[] = [
  { label: "All", value: "All", filter: {} },
];

export const ConfigDepartmentList: FC<IDepartmentsConfigListPageProps> = ({
  module,
}) => {
  const { navigate } = useRouter();

  const [search, setSearch] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] =
    useState<IDepartmentResponse | null>(null);

  const debouncedSearch = useDebounce(search);

  // TODO: Implement permissions for departments, pending addition on backend

  const {
    departments,
    loading,
    getDepartments,
    setDepartments,
    recentDepartments,
    setRecentDepartments,
    getRecentDepartments,
    setFilter,
  } = useDepartments({ debouncedSearch });

  const {
    loading: isDepartmentLoading,
    deleteDepartment,
    refresh,
  } = useDepartment();

  const { hasSomePermissions } = usePermissions();

  const canDeleteDepartment = hasSomePermissions([
    PERMISSIONS.DEPARTMENTS.DELETE,
    PERMISSIONS.SYSTEM_ADMINISTRATION.ADMINISTRATION,
  ]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleOpenDeleteModal = (item: IDepartmentResponse) => {
    setSelectedDepartment(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    if (selectedDepartment && canDeleteDepartment) {
      deleteDepartment(selectedDepartment);
      setSelectedDepartment(null);
      setOpenDeleteModal(false);
    }
  };

  const navigateToDepartmentDetails = (departmentId: string) => {
    navigate(`${CONFIG_PATHS[CONFIG_PAGES.DEPARTMENTS]}/${departmentId}`);
  };

  const navigateToAddDepartment = () => {
    navigate(CONFIG_PATHS[CONFIG_PAGES.ADD_DEPARTMENT]);
  };

  const handleTabChange = (
    tab?: IVerticalTabProps<IDepartmentResponse[]> | null
  ) => {
    setFilter(tab?.filter || {});
  };

  const moduleFilteredDepartments = module
    ? departments.filter((dept) => dept.modules?.includes(module))
    : departments;

  return (
    <ConfigPageLayout
      title="Departments"
      titleActionComponent={
        <AddButton
          label="Add New Department"
          onClick={navigateToAddDepartment}
        />
      }
      search={search}
      onHandleSearch={handleSearchChange}
      searchPlaceholder="Search"
      hideOnBack
      loading={loading || isDepartmentLoading}
    >
      <VerticalTabsLayout
        hideNavigationButtons
        hideStatusIcons
        tabs={departmentListTabs}
        onTabChange={handleTabChange}
      >
        <MiList
          viewType="card"
          data={moduleFilteredDepartments}
          dataProvider={getDepartments}
          setData={setDepartments}
          recentData={recentDepartments}
          setRecentData={setRecentDepartments}
          recentDataProvider={getRecentDepartments}
          CardComponent={ConfigDepartmentListCard}
          cardProps={{
            onEdit: navigateToDepartmentDetails,
            onDelete: canDeleteDepartment ? handleOpenDeleteModal : undefined,
          }}
          paginationMode={PAGINATION.SERVER}
          refresh={refresh}
          entityName="Departments"
          loading={loading || isDepartmentLoading}
          search={debouncedSearch}
          emptyListProps={{
            text: "No Departments found",
          }}
        />
      </VerticalTabsLayout>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={() => setOpenDeleteModal(false)}
        handlePositive={handleConfirmDeleteModal}
        description={`You are about to delete ${selectedDepartment?.name}. Are you sure you would like to continue?`}
        buttonPositiveColor="error"
      />
    </ConfigPageLayout>
  );
};

interface IDepartmentsConfigListPageProps {
  module?: PlatformModules;
}
